import { useEffect, useMemo, useRef } from "react";

const CONFETTI_COLORS = ["#ef4444", "#f59e0b", "#eab308", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];
const BALLOONS = ["🎈", "🎈", "🎈", "🎉", "🎊"];

// Full-screen, one-shot celebration shown after a new member signs up:
// confetti "crackers" rain down while balloons float up. Pointer-events-none so
// it never blocks the UI; auto-dismisses via onDone.
export default function WelcomeFlowers({ show, name, onDone, duration = 5000 }) {
  // Keep the latest onDone in a ref so the auto-dismiss timer is NOT reset on
  // every parent re-render. The feed polls frequently; if `onDone` (a new inline
  // function each render) were in the effect deps, the timeout would clear+reset
  // on every render and never fire — leaving the celebration overlay stuck.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => onDoneRef.current && onDoneRef.current(), duration);
    return () => clearTimeout(t);
  }, [show, duration]);

  const confetti = useMemo(() => {
    if (!show) return [];
    return Array.from({ length: 70 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2.2,
      dur: 2.6 + Math.random() * 3,
      w: 6 + Math.random() * 7,
      h: 8 + Math.random() * 8,
      drift: Math.round((Math.random() * 2 - 1) * 120),
      rot: Math.round((Math.random() * 2 - 1) * 720),
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      round: Math.random() > 0.6,
    }));
  }, [show]);

  const balloons = useMemo(() => {
    if (!show) return [];
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      left: 4 + Math.random() * 92,
      delay: Math.random() * 1.8,
      dur: 4.5 + Math.random() * 3,
      size: 30 + Math.random() * 26,
      sway: Math.round((Math.random() * 2 - 1) * 40),
      balloon: BALLOONS[i % BALLOONS.length],
    }));
  }, [show]);

  if (!show) return null;

  return (
    <div className="wf-overlay" aria-hidden="true">
      <style>{`
        @keyframes wf-confetti {
          0%   { transform: translate(0, -12vh) rotate(0deg); opacity: 0; }
          8%   { opacity: 1; }
          100% { transform: translate(var(--wf-drift, 0px), 112vh) rotate(var(--wf-rot, 720deg)); opacity: 1; }
        }
        @keyframes wf-rise {
          0%   { transform: translate(0, 16vh) scale(0.8); opacity: 0; }
          12%  { opacity: 1; }
          100% { transform: translate(var(--wf-sway, 0px), -120vh) scale(1); opacity: 1; }
        }
        @keyframes wf-pop {
          0%   { transform: translateX(-50%) scale(0.6); opacity: 0; }
          12%  { transform: translateX(-50%) scale(1.08); opacity: 1; }
          82%  { transform: translateX(-50%) scale(1); opacity: 1; }
          100% { transform: translateX(-50%) scale(0.96); opacity: 0; }
        }
        .wf-overlay { position: fixed; inset: 0; z-index: 9999; pointer-events: none; overflow: hidden; }
        .wf-confetti { position: absolute; top: 0; border-radius: 2px; will-change: transform, opacity; animation-name: wf-confetti; animation-timing-function: linear; animation-fill-mode: forwards; }
        .wf-balloon { position: absolute; bottom: 0; line-height: 1; will-change: transform, opacity; animation-name: wf-rise; animation-timing-function: ease-in; animation-fill-mode: forwards; }
        .wf-banner { position: absolute; top: 16%; left: 50%; will-change: transform, opacity; animation-name: wf-pop; animation-timing-function: ease-in-out; animation-fill-mode: forwards; }
      `}</style>

      {confetti.map((c) => (
        <span
          key={`c${c.id}`}
          className="wf-confetti"
          style={{
            left: `${c.left}vw`,
            width: `${c.w}px`,
            height: `${c.h}px`,
            backgroundColor: c.color,
            borderRadius: c.round ? "9999px" : "2px",
            animationDelay: `${c.delay}s`,
            animationDuration: `${c.dur}s`,
            "--wf-drift": `${c.drift}px`,
            "--wf-rot": `${c.rot}deg`,
          }}
        />
      ))}

      {balloons.map((b) => (
        <span
          key={`b${b.id}`}
          className="wf-balloon"
          style={{
            left: `${b.left}vw`,
            fontSize: `${b.size}px`,
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.dur}s`,
            "--wf-sway": `${b.sway}px`,
          }}
        >
          {b.balloon}
        </span>
      ))}

      <div className="wf-banner" style={{ animationDuration: `${duration}ms` }}>
        <div className="flex items-center gap-2.5 rounded-full bg-surface/95 px-6 py-3 shadow-2xl ring-1 ring-black/5 backdrop-blur">
          <span className="text-2xl">🎉</span>
          <span className="text-lg font-extrabold text-gray-900">
            Welcome{name ? `, ${String(name).split(" ")[0]}` : ""}! 🎈
          </span>
          <span className="text-2xl">🎊</span>
        </div>
      </div>
    </div>
  );
}
